import { X, Plus, Save } from "lucide-react";
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { DocumentsTableContent } from './DocumentsTableContent';
import { useSelector, useDispatch } from 'react-redux';
import { setExtractedData, setLastEditedDocumentField, clearLastEditedDocumentField } from '../../../store/reducers/index';
import { getDeclarationSummaryTranslation, getMainTranslation } from '../../../hooks/useLanguage';
import reduxStore from '../../../store/store';
import { applyDocumentFieldToAllPartidas } from '../../../utils/applyToAllDocuments';
import { deepClone } from '../../../utils/dataHelpers';

export const DocumentsTableModal = ({
  isOpen,
  onClose,
  declarationIndex,
  itemIndex,
  isSpanish,
  item,
  editableData,
  setEditableData,
  setIsDirty,
}) => {
  const [openDocModalIndex, setOpenDocModalIndex] = useState(null);
  const [openTipoModal, setOpenTipoModal] = useState({});
  const [originalItemData, setOriginalItemData] = useState(null);
  const [isModalDirty, setIsModalDirty] = useState(false);
  const prevIsOpenRef = useRef(false);
  const prevEditableDataRef = useRef(null);
  const isInternalChangeRef = useRef(false);
  const documentsTableContentRef = useRef(null);
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company);
  const userData = useSelector((state) => state.user.userData);
  const extractedData = useSelector((state) => state.documents.extractedData);
  const lastEditedDocumentField = useSelector(state => state.documents.lastEditedDocumentField);
  const lastEditedDocumentValue = useSelector(state => state.documents.lastEditedDocumentValue);
  const lastEditedDocumentDeclarationIndex = useSelector(state => state.documents.lastEditedDocumentDeclarationIndex);
  const lastEditedDocumentPartidaIndex = useSelector(state => state.documents.lastEditedDocumentPartidaIndex);
  const lastEditedDocumentDocumentIndex = useSelector(state => state.documents.lastEditedDocumentDocumentIndex);
  const companyName = company?.nombre?.toLowerCase() || userData?.first_name?.toLowerCase();
  const declaration = editableData?.[declarationIndex];
  const canApplyDocumentsToAll = Array.isArray(declaration?.partidas) && declaration.partidas.length > 1;
  const canShowApplyButton =
    !!lastEditedDocumentField &&
    lastEditedDocumentDeclarationIndex === declarationIndex &&
    lastEditedDocumentPartidaIndex === itemIndex &&
    lastEditedDocumentDocumentIndex !== null &&
    lastEditedDocumentDocumentIndex !== undefined;
  
  const isApplyButtonEnabled = canShowApplyButton && canApplyDocumentsToAll;
  
  // Store original item data when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      // Modal just opened - capture original data
      const currentItem = editableData[declarationIndex]?.partidas?.[itemIndex];
      if (currentItem) {
        const documentos = deepClone(currentItem.documentos || []);
        setOriginalItemData(documentos);
        prevEditableDataRef.current = JSON.stringify(documentos);
        setIsModalDirty(false);
      }
    } else if (!isOpen && prevIsOpenRef.current) {
      // Modal just closed - reset
      setOriginalItemData(null);
      prevEditableDataRef.current = null;
      setIsModalDirty(false);
      isInternalChangeRef.current = false;
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, declarationIndex, itemIndex, editableData]);
  
  // Update originalItemData when editableData changes externally (e.g., "Apply to All" clicked) This ensures the save/cancel buttons disappear after "Apply to All"
  useEffect(() => {
    if (isOpen && originalItemData !== null && !isInternalChangeRef.current) {
      const currentItem = editableData[declarationIndex]?.partidas?.[itemIndex];
      if (currentItem) {
        const currentDocumentos = currentItem.documentos || [];
        const currentDataString = JSON.stringify(currentDocumentos);
        const prevDataString = prevEditableDataRef.current;
        
        // If data changed and it wasn't an internal change, update originalItemData This handles "Apply to All" and other external changes
        if (currentDataString !== prevDataString) {
          setOriginalItemData(deepClone(currentDocumentos));
          prevEditableDataRef.current = currentDataString;
          setIsModalDirty(false);
        }
      }
    }
    // Reset the internal change flag after processing
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
    }
  }, [editableData, isOpen, declarationIndex, itemIndex, originalItemData]);

  // Track changes in the modal
  useEffect(() => {
    if (isOpen && originalItemData !== null) {
      const currentItem = editableData[declarationIndex]?.partidas?.[itemIndex];
      if (currentItem) {
        const currentDocumentos = currentItem.documentos || [];
        const hasChanges = JSON.stringify(currentDocumentos) !== JSON.stringify(originalItemData);
        setIsModalDirty(hasChanges);
      }
    }
  }, [isOpen, editableData, declarationIndex, itemIndex, originalItemData]);

  const handleSave = () => {
    // Mark as internal change to prevent the external change handler from interfering
    isInternalChangeRef.current = true;
    // CRITICAL: Update extractedData FIRST so that updateEditableData can detect this as a save operation This ensures originalDataRef is updated to the saved state for proper cancel functionality
    dispatch(setExtractedData(editableData));
    // Then update editableData - this will detect the save and update originalDataRef
    setEditableData(editableData);
    setIsDirty(false);
    
    // Update originalItemData to current state so buttons disappear
    const currentItem = editableData[declarationIndex]?.partidas?.[itemIndex];
    if (currentItem) {
      const documentos = deepClone(currentItem.documentos || []);
      setOriginalItemData(documentos);
      prevEditableDataRef.current = JSON.stringify(documentos);
      setIsModalDirty(false);
    }
    toast.success(getMainTranslation('changesSaved', isSpanish));
  };

  const handleCancel = () => {
    if (originalItemData === null) {
      setIsModalDirty(false);
      dispatch(clearLastEditedDocumentField());
      return;
    }
    
    isInternalChangeRef.current = true;
    const updatedData = deepClone(editableData);
    const partida = updatedData[declarationIndex]?.partidas?.[itemIndex];
    
    if (partida) {
      partida.documentos = deepClone(originalItemData);
      prevEditableDataRef.current = JSON.stringify(partida.documentos || []);
      setEditableData(updatedData);
    }
    
    setIsModalDirty(false);
    dispatch(clearLastEditedDocumentField());
    toast.success(getMainTranslation('changesCancelled', isSpanish));
  };
  
  // Wrapper for setEditableData that marks changes as internal
  const handleSetEditableData = (newData) => {
    isInternalChangeRef.current = true;
    setEditableData(newData);
  };

  const handleApplyDocumentsToAllPartidas = () => {
    if (!canApplyDocumentsToAll || !canShowApplyButton) return;
    
    isInternalChangeRef.current = true;
    
    let latestEditableData = editableData;
    if (documentsTableContentRef.current?.flushPendingUpdates) {
      const flushedData = documentsTableContentRef.current.flushPendingUpdates();
      if (flushedData) {
        latestEditableData = flushedData;
      } else {
        const state = reduxStore.getState();
        latestEditableData = state?.documents?.editableData || editableData;
      }
    } else {
      const state = reduxStore.getState();
      latestEditableData = state?.documents?.editableData || editableData;
    }
    
    const valueToApply = lastEditedDocumentValue !== undefined && lastEditedDocumentValue !== null
      ? lastEditedDocumentValue
      : latestEditableData[declarationIndex]?.partidas?.[itemIndex]?.documentos?.[lastEditedDocumentDocumentIndex]?.[lastEditedDocumentField];
    
    if (valueToApply === undefined || !lastEditedDocumentField) {
      return;
    }
    
    const updatedData = applyDocumentFieldToAllPartidas(
      latestEditableData,
      declarationIndex,
      itemIndex,
      lastEditedDocumentDocumentIndex,
      lastEditedDocumentField,
      valueToApply
    );
    
    if (updatedData === latestEditableData) return;
    
    handleSetEditableData(updatedData);
    setIsDirty(true);
    
    const updatedSourcePartida = updatedData[declarationIndex]?.partidas?.[itemIndex];
    if (updatedSourcePartida?.documentos) {
      const syncedDocuments = deepClone(updatedSourcePartida.documentos);
      setOriginalItemData(syncedDocuments);
      prevEditableDataRef.current = JSON.stringify(syncedDocuments);
      setIsModalDirty(false);
    }
    
    dispatch(clearLastEditedDocumentField());
  };

  // Handler for adding Y025 document for TuDeFrigo
  const handleAddY025TuDeFrigo = () => {
    // Mark as internal change
    isInternalChangeRef.current = true;
    // Deep clone to avoid mutating original data
    const updatedData = deepClone(editableData);
    if (!updatedData[declarationIndex].partidas[itemIndex].documentos)
      updatedData[declarationIndex].partidas[itemIndex].documentos = [];
    updatedData[declarationIndex].partidas[itemIndex].documentos.push({
      codigo_documento: 'Y025',
      tipo: 1,
      numero: 'ESAEOF240000022A',
      fecha: '23-01-2024',
    });
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field: '__documents__',
      value: null,
      declarationIndex,
      partidaIndex: itemIndex,
      documentIndex: updatedData[declarationIndex].partidas[itemIndex].documentos.length - 1,
    }));
  };

  // Handler for adding Y025 document for El Mosca
  const handleAddY025ElMosca = () => {
    // Mark as internal change
    isInternalChangeRef.current = true;
    // Deep clone to avoid mutating original data
    const updatedData = deepClone(editableData);
    if (!updatedData[declarationIndex].partidas[itemIndex].documentos)
      updatedData[declarationIndex].partidas[itemIndex].documentos = [];
    updatedData[declarationIndex].partidas[itemIndex].documentos.push({
      codigo_documento: 'Y025',
      tipo: 1,
      numero: 'ESAEOF240000022A',
      fecha: '23-01-2024',
    });
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field: '__documents__',
      value: null,
      declarationIndex,
      partidaIndex: itemIndex,
      documentIndex: updatedData[declarationIndex].partidas[itemIndex].documentos.length - 1,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
      <div className="bg-white rounded-lg p-6 w-full max-w-[95vw] h-[90vh] flex flex-col animate-modalEnter">
        {/* Header */}
        <div className="flex justify-between items-center mb-4 pb-4 border-b">
          <h2 className="text-2xl font-bold">
            {!isSpanish ? "Documents" : "Documentos"} ({item.documentos?.length || 0})
          </h2>
          <div className="flex items-center gap-2">
            {/* Save/Cancel Buttons */}
            {isModalDirty && (
              <>
                <button
                  className="bg-red-50 rounded-full flex items-center py-2 px-3 gap-2"
                  onClick={handleCancel}
                  title={!isSpanish ? "Cancel Changes" : "Cancelar Cambios"}
                >
                  <X className="size-4 stroke-red-500" />
                  <span className="text-xs text-red-500">{getDeclarationSummaryTranslation('cancel', isSpanish)}</span>
                </button>
                <button
                  className="bg-primary rounded-full flex items-center py-2 px-3 gap-2"
                  onClick={handleSave}
                  title={!isSpanish ? "Save Changes" : "Guardar Cambios"}
                >
                  <Save className="size-4" />
                  <span className="text-xs">{getDeclarationSummaryTranslation('save', isSpanish)}</span>
                </button>
                {canShowApplyButton && (
            <button
              className="py-2 text-xs flex items-center gap-2 rounded-full px-3 bg-primary disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleApplyDocumentsToAllPartidas}
              title={getDeclarationSummaryTranslation('saveAllDocumentsTooltip', isSpanish)}
              disabled={!isApplyButtonEnabled}
              type="button"
            >
               <Save className="size-4" />
              <span className="text-xs">{getDeclarationSummaryTranslation('saveAllDocuments', isSpanish)}</span>
            </button>
                )}
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title={!isSpanish ? "Close" : "Cerrar"}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 items-center mb-4 justify-end">
          <button
            className="py-1 text-xs flex items-center gap-1"
            onClick={() => {
              // Mark as internal change
              isInternalChangeRef.current = true;
              // Deep clone to avoid mutating original data
              const updatedData = deepClone(editableData);
              if (!updatedData[declarationIndex].partidas[itemIndex].documentos)
                updatedData[declarationIndex].partidas[itemIndex].documentos = [];
              updatedData[declarationIndex].partidas[itemIndex].documentos.push({
                codigo_documento: "",
                tipo: "",
                numero: "",
                fecha: "",
              });
              setEditableData(updatedData);
              setIsDirty(true);
              dispatch(setLastEditedDocumentField({
                field: '__documents__',
                value: null,
                declarationIndex,
                partidaIndex: itemIndex,
                documentIndex: updatedData[declarationIndex].partidas[itemIndex].documentos.length - 1,
              }));
            }}
          >
            <Plus className={`size-4`} />
            {isSpanish ? "Agregar Documento" : "Add Document"}
          </button>
          {/* Client-specific Y025 buttons */}
          {companyName === 'tudefrigo' && (
            <button
              className="py-1.5 text-xs flex items-center gap-1 bg-secondary rounded-full pl-2 pr-3"
              onClick={handleAddY025TuDeFrigo}
              type="button"
            >
              <Plus className="size-4" />
              Agregar Y025
            </button>
          )}
          {companyName === 'el mosca' && (
            <button
              className="py-1.5 text-xs flex items-center gap-1 bg-secondary rounded-full pl-2 pr-3"
              onClick={handleAddY025ElMosca}
              type="button"
            >
              <Plus className="size-4" />
              Agregar Y025
            </button>
          )}
        </div>

        {/* Table Content */}
        <div className="flex-1 overflow-auto">
          <DocumentsTableContent
            ref={documentsTableContentRef}
            declarationIndex={declarationIndex}
            itemIndex={itemIndex}
            isSpanish={isSpanish}
            item={item}
            editableData={editableData}
            setEditableData={handleSetEditableData}
            setIsDirty={setIsDirty}
            openDocModalIndex={openDocModalIndex}
            setOpenDocModalIndex={setOpenDocModalIndex}
            openTipoModal={openTipoModal}
            setOpenTipoModal={setOpenTipoModal}
          />
        </div>
      </div>
    </div>
  );
};

